import React, { ReactElement } from 'react';
import styled from '@emotion/styled';
import { profilePhoto } from '../../actions/profileTrackAction';
import { HeartNotificationIcon, OfferNew } from '../icons/favoriteIcons';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { areEqual } from '../../utils/helpers';
import breakpoints from '../../theme/material/breakpoints';

const PhotoContainer = styled('div')({
  width: '100%',
  height: '100%',
  aspectRatio: '3/4',
  borderRadius: 6,
  overflow: 'hidden',
  position: 'relative',
  cursor: 'pointer',
  display: 'flex',

  // Fallback for browsers that don't support aspect-ratio
  '@supports not (aspect-ratio: 3/4)': {
    paddingTop: '133%',
    height: 0,
  },

  [breakpoints.down(479.05)]: {
    maxWidth: '100%',
    aspectRatio: '3/4',
    borderRadius: 0,

    // Fallback for mobile
    '@supports not (aspect-ratio: 3/4)': {
      paddingTop: '133%',
      height: 0,
    },
  },
});

const GradientBottom = styled('div')({
  [breakpoints.down(479.05)]: {
    background:
      'rgba(0, 0, 0, 0) linear-gradient(to bottom, rgba(0, 0, 0,0.2) 5%, rgba(255, 255, 255, 0) 15%,rgba(255, 255, 255, 0) 80%, rgba(0, 0, 0,0.7) 100%) repeat scroll 0% 0%',
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    zIndex: 2,
  },
});

const Photo = styled('div')<{ backgroundImage: string }>(
  {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
  },
  ({ backgroundImage }) =>
    backgroundImage && {
      backgroundImage: `url(${backgroundImage})`,
      backgroundPosition: 'top',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
    }
);

const ProfileCardBanner = styled('div')<{ customBackground: string }>(
  {
    position: 'absolute',
    top: 0,
    left: 0,
    padding: '6px 0',
    width: '100%',
    opacity: 0.9,
    zIndex: 5,
    display: 'flex',
    justifyContent: 'center',
    fontSize: 14,
    color: '#fff',
    svg: {
      width: 14,
      height: 14,
      marginTop: 1,
      marginRight: 4,
    },
    [breakpoints.down(639.05)]: {
      fontSize: 12,
      svg: {
        height: 12,
      },
    },
  },
  ({ customBackground }) => ({
    background: `${customBackground ? customBackground : 'rgba(188,32,40,0.9)'}`,
  })
);

interface ProfilePhotoProps {
  imgUrl: string;
  onClick: any;
  offer: any;
  myProfile: any;
  isOtherProfile?: boolean;
}

const ProfilePhoto = ({ imgUrl, onClick, offer, myProfile, isOtherProfile }: ProfilePhotoProps): ReactElement => {
  const renderProfileCardBanner = () => {
    if (offer && offer.status) {
      if (offer.status === 'accepted') {
        return (
          <ProfileCardBanner
            data-test-id="profile-banner-offer-accepted"
            customBackground="linear-gradient(93deg, #10CBBB 0%, #2B8FD7 100%)"
          >
            <HeartNotificationIcon /> Offer Accepted!
          </ProfileCardBanner>
        );
      }

      if (offer.status === 'pending') {
        if (offer.to_account_hash_id === myProfile?.hash_id) {
          return (
            <ProfileCardBanner
              data-test-id={offer.suggest ? 'profile-banner-suggest-request' : 'profile-banner-offer-request'}
              customBackground="rgb(191, 146, 0)"
            >
              {offer.suggest ? 'Asked you to Offer!' : 'Sent You an Offer!'}
            </ProfileCardBanner>
          );
        }

        return (
          <ProfileCardBanner data-test-id="profile-banner-pending-offer" customBackground="rgb(191, 146, 0)">
            <OfferNew fill="rgba(255,255,255,1)" /> Pending Offer
          </ProfileCardBanner>
        );
      }
    }

    return '';
  };

  return (
    <PhotoContainer
      onClick={e => {
        profilePhoto();
        e.preventDefault();
        onClick(0, e);

        if (myProfile?.hash_id) {
          if (isOtherProfile) {
            mixpanelTrackEvent({
              eventName: EVENT_NAMES.LIGHTBOX_OPENED,
              payload: {
                eventProperties: {
                  'Lightbox Source': 'Main Photo',
                },
              },
            });
          }
        }
      }}
    >
      {renderProfileCardBanner()}
      <Photo data-test-id="profile-photo-container" backgroundImage={imgUrl} />
      <GradientBottom />
    </PhotoContainer>
  );
};

export default React.memo(ProfilePhoto, areEqual);
